/**
 * BodyPlanOverlay — pause-time evolution status card.
 *
 * Shown by CambrianStage whenever the user pauses. Lists every body part
 * with its level (grown ones lit, the rest dim); hovering one shows what
 * it does now and what the next upgrade adds.
 */

import { MAX_PART_LEVEL, PARTS, partIconSvg, partLevel, type PartDef, type PartLevels } from './parts';
import { IS_TOUCH } from '@/ui/TouchControls';

export class BodyPlanOverlay {
  private root: HTMLElement | null = null;
  private getParts: () => PartLevels;

  constructor(getParts: () => PartLevels) {
    this.getParts = getParts;
  }

  open(): void {
    if (this.root) return;
    this.injectStyle();
    const parts = this.getParts();
    const grown = PARTS.filter((p) => partLevel(parts, p.id) > 0).length;

    const root = document.createElement('div');
    root.id = 'cambrian-bodyplan';
    root.innerHTML = `
      <div class="bp-card">
        <div class="bp-header">
          <div class="bp-sub">PAUSED · EVOLUTION STATUS</div>
          <div class="bp-title">Body Plan</div>
          <div class="bp-count">${grown} of ${PARTS.length} parts grown</div>
        </div>
        <div class="bp-grid"></div>
        <div class="bp-info"><div class="bp-info-hint">${IS_TOUCH ? 'Tap' : 'Hover'} a part to see what it does</div></div>
      </div>
    `;
    document.body.appendChild(root);
    this.root = root;

    const grid = root.querySelector('.bp-grid') as HTMLElement;
    const info = root.querySelector('.bp-info') as HTMLElement;
    const hint = info.innerHTML;
    for (const def of PARTS) {
      const lv = partLevel(parts, def.id);
      const hex = `#${def.color.toString(16).padStart(6, '0')}`;
      const tile = document.createElement('div');
      tile.className = 'bp-part' + (lv > 0 ? ' on' : '');
      tile.style.setProperty('--mc', hex);
      const pips = Array.from({ length: MAX_PART_LEVEL }, (_, i) => `<span class="bp-pip${i < lv ? ' on' : ''}"></span>`).join('');
      tile.innerHTML = `
        <div class="bp-icon">${partIconSvg(def.id, def.color)}</div>
        <div class="bp-name">${def.name}</div>
        <div class="bp-pips">${pips}</div>
      `;
      const show = (): void => { info.innerHTML = this.infoHtml(def, lv); };
      tile.addEventListener('mouseenter', show);
      tile.addEventListener('click', show);   // touch: tap a part to read it
      grid.appendChild(tile);
    }
    grid.addEventListener('mouseleave', () => { info.innerHTML = hint; });
  }

  close(): void {
    this.root?.remove();
    this.root = null;
  }

  private infoHtml(def: PartDef, lv: number): string {
    const hex = `#${def.color.toString(16).padStart(6, '0')}`;
    const now = lv > 0 ? `<div class="bp-info-now">Lv ${lv}: ${def.levels[lv - 1]}</div>` : '<div class="bp-info-now dim">Not grown yet</div>';
    const next = lv < MAX_PART_LEVEL
      ? `<div class="bp-info-next">${lv > 0 ? 'Next' : 'Lv 1'}: ${def.levels[lv]}</div>`
      : '<div class="bp-info-next">Fully evolved</div>';
    return `
      <div class="bp-info-name" style="color:${hex}">${def.name}<span class="bp-info-spot">${def.spot.toUpperCase()}</span></div>
      <div class="bp-info-desc">${def.blurb}</div>
      ${now}${next}
    `;
  }

  private injectStyle(): void {
    if (document.getElementById('cambrian-bodyplan-styles')) return;
    const style = document.createElement('style');
    style.id = 'cambrian-bodyplan-styles';
    style.textContent = `
#cambrian-bodyplan {
  position: fixed; left: 24px; top: 50%; transform: translateY(-50%);
  z-index: 210; font-family: 'Inter', system-ui, sans-serif; color: #fff5dc;
  pointer-events: none;
}
#cambrian-bodyplan .bp-card {
  background: rgba(18, 8, 4, 0.94);
  border: 1px solid rgba(255, 200, 120, 0.28);
  border-radius: 16px; padding: 20px 22px; width: 460px;
  box-shadow: 0 24px 60px rgba(0,0,0,0.55);
  backdrop-filter: blur(8px); -webkit-backdrop-filter: blur(8px);
  pointer-events: auto;
}
#cambrian-bodyplan .bp-header { text-align: center; margin-bottom: 16px; }
#cambrian-bodyplan .bp-sub { font-size: 10px; letter-spacing: 0.4em; opacity: 0.55; }
#cambrian-bodyplan .bp-title { font-size: 24px; font-weight: 800; letter-spacing: 0.16em; color: #ffd070; text-shadow: 0 0 14px rgba(255, 190, 90, 0.35); margin-top: 2px; }
#cambrian-bodyplan .bp-count { font-size: 11px; opacity: 0.7; margin-top: 4px; letter-spacing: 0.08em; }
#cambrian-bodyplan .bp-grid { display: grid; grid-template-columns: repeat(4, 1fr); gap: 8px; }
#cambrian-bodyplan .bp-part {
  display: flex; flex-direction: column; align-items: center; gap: 4px;
  padding: 10px 6px; border-radius: 10px; cursor: help;
  background: rgba(40, 18, 10, 0.45); border: 1px solid rgba(255, 200, 120, 0.1);
  opacity: 0.4; transition: background 0.12s, opacity 0.12s;
}
#cambrian-bodyplan .bp-part.on { opacity: 1; border-color: var(--mc); background: rgba(0,0,0,0.35); }
#cambrian-bodyplan .bp-part:hover { opacity: 1; background: rgba(255, 220, 160, 0.1); }
#cambrian-bodyplan .bp-icon { width: 38px; height: 38px; }
#cambrian-bodyplan .bp-name { font-size: 10px; font-weight: 700; text-align: center; line-height: 1.15; }
#cambrian-bodyplan .bp-pips { display: flex; gap: 4px; }
#cambrian-bodyplan .bp-pip { width: 7px; height: 7px; border-radius: 50%; border: 1px solid var(--mc); }
#cambrian-bodyplan .bp-pip.on { background: var(--mc); }
#cambrian-bodyplan .bp-info {
  margin-top: 14px; min-height: 92px; padding: 12px 14px; border-radius: 10px;
  background: rgba(0,0,0,0.35); border: 1px solid rgba(255, 200, 120, 0.14);
}
#cambrian-bodyplan .bp-info-hint { font-size: 11px; opacity: 0.45; text-align: center; padding-top: 26px; letter-spacing: 0.08em; }
#cambrian-bodyplan .bp-info-name { font-size: 15px; font-weight: 800; }
#cambrian-bodyplan .bp-info-spot { font-size: 9px; font-weight: 600; opacity: 0.6; letter-spacing: 0.18em; margin-left: 8px; color: #fff5dc; }
#cambrian-bodyplan .bp-info-desc { font-size: 12px; margin-top: 4px; line-height: 1.35; }
#cambrian-bodyplan .bp-info-now { font-size: 11px; margin-top: 6px; color: #ffd070; font-weight: 600; }
#cambrian-bodyplan .bp-info-now.dim { color: #fff5dc; opacity: 0.5; font-weight: 400; }
@media (max-width: 760px), (max-height: 500px) {
  #cambrian-bodyplan { left: 50%; transform: translate(-50%, -50%); }
  #cambrian-bodyplan .bp-card { width: min(420px, calc(100vw - 24px)); padding: 14px; max-height: 90vh; overflow-y: auto; }
  #cambrian-bodyplan .bp-title { font-size: 19px; }
  #cambrian-bodyplan .bp-icon { width: 30px; height: 30px; }
}
#cambrian-bodyplan .bp-info-next { font-size: 11px; margin-top: 3px; opacity: 0.65; }
`;
    document.head.appendChild(style);
  }
}
